import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SageIntegrationService } from '../../../../shared/services/sage-integration.service';
import {
  isSyncDisabledError,
  relativeDayLabel,
  sageErrorMessageFor,
  SageItemAdjustment,
  SageItemAdjustmentReason,
  signedQuantity,
} from '../../../../shared/services/sage-integration.model';

type RowState = 'releasing' | 'applied' | 'insync' | 'cancelled' | 'conflict';

@Component({
  selector: 'app-sage-stock-column',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './stock-column.component.html',
  styleUrls: ['./sage-work-column.css', './stock-column.component.css'],
})
export class SageStockColumnComponent {
  @Input() rows: SageItemAdjustment[] = [];
  @Input() totalCount = 0;
  @Input() reasons: SageItemAdjustmentReason[] = [];
  @Input() syncEnabled = true;
  @Input() loading = false;

  /** Something was released or dismissed — the parent refetches so resolved rows drop out. */
  @Output() changed = new EventEmitter<void>();
  @Output() viewAll = new EventEmitter<void>();
  @Output() syncDisabled = new EventEmitter<void>();

  reasonById: Record<number, number | null> = {};
  rowState: Record<number, RowState> = {};
  rowMessage: Record<number, string> = {};
  rowError: Record<number, string> = {};
  selected = new Set<number>();
  bulkReasonId: number | null = null;
  bulkReleasing = false;

  readonly relativeDayLabel = relativeDayLabel;
  readonly trackRow = (_: number, r: SageItemAdjustment) => r.id;

  constructor(private sageService: SageIntegrationService) {}

  // ---- row presentation ----

  /** What the release will do, so the user never has to work the delta out themselves. */
  outcomeText(row: SageItemAdjustment): string {
    const target = row.direction === 1 ? row.aivorQuantity : row.sageQuantity;
    const system = row.direction === 1 ? 'Sage' : 'Aivor';
    return `${system} set to ${target} (${signedQuantity(row.delta)})`;
  }

  directionLabel(row: SageItemAdjustment): string {
    return row.direction === 1 ? 'Update Sage' : 'Update Aivor';
  }

  stateOf(row: SageItemAdjustment): RowState | null {
    return this.rowState[row.id] ?? null;
  }

  isPending(row: SageItemAdjustment): boolean {
    return !this.rowState[row.id];
  }

  canRelease(row: SageItemAdjustment): boolean {
    return this.syncEnabled && !!this.reasonById[row.id] && this.isPending(row);
  }

  releaseTooltip(row: SageItemAdjustment): string {
    if (!this.syncEnabled) return 'Sync is turned off';
    return this.reasonById[row.id] ? '' : 'Choose a reason first';
  }

  // ---- selection ----

  get selectableRows(): SageItemAdjustment[] {
    return this.rows.filter((r) => this.isPending(r));
  }

  get allSelected(): boolean {
    const pool = this.selectableRows;
    return pool.length > 0 && pool.every((r) => this.selected.has(r.id));
  }

  toggleRow(row: SageItemAdjustment): void {
    if (this.selected.has(row.id)) this.selected.delete(row.id);
    else this.selected.add(row.id);
  }

  toggleAll(): void {
    if (this.allSelected) {
      this.selected.clear();
      return;
    }
    this.selectableRows.forEach((r) => this.selected.add(r.id));
  }

  // ---- actions ----

  release(row: SageItemAdjustment): void {
    const reasonId = this.reasonById[row.id];
    if (!reasonId || !this.canRelease(row)) return;
    this.runRelease(row, reasonId);
  }

  // Parallel is safe here: at most five rows are ever visible, so this cannot approach Sage's
  // 100 req/min limit the way applyAllReviewed's entity-wide fan-out could.
  releaseSelected(): void {
    if (!this.bulkReasonId || !this.syncEnabled || this.bulkReleasing) return;
    const targets = this.rows.filter((r) => this.selected.has(r.id) && this.isPending(r));
    if (!targets.length) return;

    this.bulkReleasing = true;
    let outstanding = targets.length;
    const done = () => {
      if (--outstanding === 0) {
        this.bulkReleasing = false;
        this.selected.clear();
      }
    };

    targets.forEach((row) => {
      this.reasonById[row.id] = this.bulkReasonId;
      this.runRelease(row, this.bulkReasonId!, done);
    });
  }

  private runRelease(row: SageItemAdjustment, reasonId: number, done?: () => void): void {
    this.rowState[row.id] = 'releasing';
    delete this.rowError[row.id];

    this.sageService.releaseStockAdjustment(row.id, reasonId).subscribe({
      next: (res) => {
        if (res.status === 'AlreadyInSync') {
          this.rowState[row.id] = 'insync';
          this.rowMessage[row.id] = 'Already in sync, nothing changed';
        } else {
          const target = row.direction === 1 ? row.aivorQuantity : row.sageQuantity;
          const system = row.direction === 1 ? 'Sage' : 'Aivor';
          this.rowState[row.id] = 'applied';
          this.rowMessage[row.id] = `${system} updated to ${target} (${signedQuantity(row.delta)})`;
        }
        this.changed.emit();
        done?.();
      },
      error: (err) => {
        if (isSyncDisabledError(err)) {
          delete this.rowState[row.id];
          this.syncDisabled.emit();
          done?.();
          return;
        }
        if (err?.status === 409) {
          this.rowState[row.id] = 'conflict';
          this.rowMessage[row.id] = 'Someone else already handled this';
          done?.();
          return;
        }
        // 400 and Sage failures leave the row releasable so the user can retry after fixing it.
        delete this.rowState[row.id];
        this.rowError[row.id] =
          sageErrorMessageFor(err) ?? err?.error?.message ?? 'Could not apply this correction.';
        done?.();
      },
    });
  }

  dismiss(row: SageItemAdjustment): void {
    if (!this.isPending(row)) return;
    this.rowState[row.id] = 'releasing';
    this.sageService.cancelStockAdjustment(row.id).subscribe({
      next: () => {
        this.rowState[row.id] = 'cancelled';
        this.rowMessage[row.id] = 'Dismissed';
        this.changed.emit();
      },
      error: (err) => {
        delete this.rowState[row.id];
        this.rowError[row.id] = sageErrorMessageFor(err) ?? 'Could not dismiss this correction.';
      },
    });
  }

  reasonTextFor(row: SageItemAdjustment): string {
    const id = this.reasonById[row.id];
    return this.reasons.find((r) => r.id === id)?.description ?? '';
  }
}
